"use client";

import { useRouter } from "next/navigation";
import { useEffect, type ReactNode } from "react";

import { SpinnerFullPage } from "@/components/ui/spinner";
import { useSession } from "@/lib/auth/client";
import { usePlatformPermissions } from "@/lib/auth/permissions";
import {
  buildDestinationFromCurrentLocation,
  buildSigninUrl,
} from "@/lib/deep-linking";
import { useOrganization } from "@/lib/organizationContext";

interface AuthGuardProps {
  children: ReactNode;
  /**
   * Fallback behavior when unauthenticated:
   * - string: Redirect URL (use buildSigninUrl() for signin)
   * - ReactNode: Render this component
   * - undefined: Default to signin with current location as destination (ADR-018)
   */
  fallback?: string | ReactNode;
  loading?: ReactNode;
  onUnauthorized?: () => void;
}

/**
 * RequireAuth Guard
 *
 * Protects pages by requiring authentication. When no fallback is provided,
 * automatically redirects to signin with the current URL as the destination,
 * enabling seamless return after OAuth login (ADR-018).
 *
 * @example
 * ```tsx
 * // Auto-redirect to signin, returns to current page after auth
 * <RequireAuth>
 *   <ProtectedContent />
 * </RequireAuth>
 *
 * // Custom fallback URL
 * <RequireAuth fallback="/custom-signin">
 *   <ProtectedContent />
 * </RequireAuth>
 *
 * // Custom fallback component
 * <RequireAuth fallback={<SignInComponent />}>
 *   <ProtectedContent />
 * </RequireAuth>
 * ```
 */
export function RequireAuth({
  children,
  fallback,
  loading = <SpinnerFullPage />,
  onUnauthorized,
}: AuthGuardProps) {
  const { data: session, isPending } = useSession();
  const isAuthenticated = !!session?.user?.id;
  const router = useRouter();

  useEffect(() => {
    if (!isPending && !isAuthenticated) {
      if (onUnauthorized) {
        onUnauthorized();
      } else if (typeof fallback === "string") {
        router.push(fallback);
      } else if (fallback === undefined) {
        // Auto-capture current location and build signin URL (ADR-018)
        const destination = buildDestinationFromCurrentLocation();
        const signinUrl = buildSigninUrl(destination);
        router.push(signinUrl);
      }
    }
  }, [isPending, isAuthenticated, fallback, router, onUnauthorized]);

  if (isPending) {
    return <>{loading}</>;
  }

  if (!isAuthenticated || !session?.user) {
    return typeof fallback === "string" || fallback === undefined ? null : (
      <>{fallback}</>
    );
  }

  return <>{children}</>;
}

interface RequireOrgMemberProps extends AuthGuardProps {
  organizationId?: string;
  minRole?: "owner" | "admin" | "member";
}

export function RequireOrgMember({
  children,
  organizationId,
  minRole = "member",
  fallback = "/403",
  loading = <SpinnerFullPage />,
  onUnauthorized,
}: RequireOrgMemberProps) {
  const { data: session, isPending: authLoading } = useSession();
  const isAuthenticated = !!session?.user?.id;

  const {
    activeOrganizationId,
    organizations,
    isLoading: orgLoading,
  } = useOrganization();
  const router = useRouter();

  const isLoading = authLoading || orgLoading || !session?.user;

  const activeOrganization = organizations.find(
    (org) => org.organizationId === activeOrganizationId,
  );
  const hasMinRole =
    activeOrganization && checkMinRole(activeOrganization.memberRole, minRole);
  const isCorrectOrg =
    !organizationId || activeOrganization?.organizationId === organizationId;

  const isAuthorized =
    isAuthenticated && !!activeOrganization && hasMinRole && isCorrectOrg;

  useEffect(() => {
    if (!isLoading && !isAuthorized) {
      if (onUnauthorized) {
        onUnauthorized();
      } else if (typeof fallback === "string") {
        router.push(fallback);
      }
    }
  }, [isLoading, isAuthorized, fallback, router, onUnauthorized]);

  if (isLoading) {
    return <>{loading}</>;
  }

  if (!isAuthorized) {
    return typeof fallback === "string" ? null : <>{fallback}</>;
  }

  return <>{children}</>;
}

function checkMinRole(
  userRole: string | undefined,
  minRole: "owner" | "admin" | "member",
): boolean {
  if (!userRole) return false;
  const roleHierarchy: Record<string, number> = {
    owner: 3,
    admin: 2,
    member: 1,
  };
  return (roleHierarchy[userRole] ?? 0) >= (roleHierarchy[minRole] ?? 0);
}

/**
 * Require Platform Admin Guard
 *
 * Protects admin pages by checking if the user has platform admin access.
 * Checks the actual user's role (not the impersonator's role during impersonation).
 *
 * Requirements:
 * - User.role must include "admin"
 * - User must be a member of "platform" organization
 *
 * This prevents impersonated non-admin users from accessing /admin/* pages.
 *
 * @example
 * ```tsx
 * export default function AdminPage() {
 *   return (
 *     <RequirePlatformAdmin>
 *       <div>Admin content</div>
 *     </RequirePlatformAdmin>
 *   );
 * }
 * ```
 */
export function RequirePlatformAdmin({
  children,
  fallback = "/",
  loading = <SpinnerFullPage />,
  onUnauthorized,
}: AuthGuardProps) {
  const { isPlatformAdmin, isPending: permissionsLoading } =
    usePlatformPermissions();
  const router = useRouter();

  // Loading
  if (permissionsLoading) {
    return <>{loading}</>;
  }

  // Done loading, so check if user is platform admin
  if (!isPlatformAdmin) {
    if (onUnauthorized) {
      onUnauthorized();
      return null;
    } else if (typeof fallback === "string") {
      router.push(fallback);
      return null;
    }
    // Fallback to null if neither condition met
    return typeof fallback === "string" ? null : <>{fallback}</>;
  }

  // User is platform admin, so return children
  return <>{children}</>;
}

export function ForbiddenPage() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <h1 className="text-4xl font-bold">403</h1>
      <p className="text-muted-foreground">
        You don&apos;t have permission to access this page.
      </p>
    </div>
  );
}
